import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["zipcode", "number", "street", "city", "country", "status"]

  static values = {
    url: String,
    searchingMessage: String,
    notFoundMessage: String,
    errorMessage: String
  }

  disconnect() {
    clearTimeout(this.timeout)
    this.request?.abort()
  }

  connect() {
    this.lookup(1000);
  }

  scheduleLookup() {
    clearTimeout(this.timeout)
    this.timeout = setTimeout(() => this.lookup(0), 400)
  }

  async lookup(delay) {
    const postcode = this.zipcodeTarget.value.replace(/\s/g, "").toUpperCase()
    const number = this.numberTarget.value.trim()

    if (!/^\d{4}[A-Z]{2}$/.test(postcode) || !number || !this.isDutchAddress()) return

    const query = `${postcode}:${number.toLowerCase()}`
    if (query === this.lastQuery) return

    this.lastQuery = query
    this.request?.abort()
    this.request = new AbortController()
    this.showStatus(this.searchingMessageValue, "text-muted")

    try {
      const url = new URL(this.urlValue, window.location.origin)
      url.searchParams.set("postcode", postcode)
      url.searchParams.set("number", number)

      await new Promise(resolve => setTimeout(resolve, delay))

      const response = await fetch(url, {
        headers: { Accept: "application/json" },
        credentials: "same-origin",
        signal: this.request.signal
      })

      if (response.status === 404) {
        this.showStatus(this.notFoundMessageValue, "text-warning")
        return
      }

      if (!response.ok) throw new Error(`Address lookup failed with HTTP ${response.status}`)

      const address = await response.json()
      this.zipcodeTarget.value = address.zipcode
      this.numberTarget.value = address.number
      this.streetTarget.value = address.street
      this.cityTarget.value = address.city
      this.countryTarget.value = address.country
      this.showStatus("address is valid!", "text-success")
    } catch (error) {
      if (error.name !== "AbortError") this.showStatus(this.errorMessageValue, "text-danger")
    }
  }

  isDutchAddress() {
    const country = this.countryTarget.value.trim().toLowerCase()
    return !country || ["nl", "nederland", "netherlands", "the netherlands"].includes(country)
  }

  showStatus(message, className) {
    this.statusTarget.textContent = message
    this.statusTarget.className = `form-text ${className}`
  }
}
